/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The management dashboard's figures, worked out from what the Swift API lists (transfers,
 * deployments, executions, endpoints, Swift Agents, schedules) and the bytes each finished sync
 * copied (its "complete, 29.9 GiB copied" disk lines). Plain functions of their input, no fetching.
 */

import type { DeploymentItem, TransferItem } from "@src/@types/MainItem";
import { getTransferItemTitle } from "@src/@types/MainItem";
import type { Execution } from "@src/@types/Execution";
import type { Endpoint } from "@src/@types/Endpoint";
import type { MinionPool } from "@src/@types/MinionPool";
import type { Schedule, ScheduleInfo } from "@src/@types/Schedule";
import { knownSizeOf } from "@src/components/modules/LiveModule/LiveStore";
import { formatAgo } from "@src/components/modules/LiveModule/liveUtils";
import {
  formatDuration,
  isActiveStatus,
  isCancelledStatus,
  isFailedStatus,
  parseApiDate,
} from "@src/utils/TransferProgress";

export const DAY_MS = 24 * 60 * 60 * 1000;
export const DAYS = 30;

export type EstateInput = {
  now: number;
  transfers: TransferItem[];
  deployments: DeploymentItem[];
  endpoints: Endpoint[];
  pools: MinionPool[];
  /** Transfer id -> its recent executions. */
  executions: { [transferId: string]: Execution[] };
  /** Execution id -> bytes the run copied (finished runs whose tasks were read). */
  copied: { [executionId: string]: number };
  schedules: { [transferId: string]: Schedule[] };
  invalidEndpoints?: Endpoint[];
  providerLabel: (type: string | null | undefined) => string;
};

export type DayRow = {
  day: number;
  completed: number;
  failed: number;
  running: number;
  bytes: number;
};

export type AttentionItem = {
  severity: "critical" | "warning" | "info";
  text: string;
  detail?: string;
  link: string;
  action: string;
};

export type RouteRow = {
  source: string;
  target: string;
  transfers: number;
  machines: number;
};

export type ActivityRow = {
  at: number;
  text: string;
  detail: string;
  tone: "success" | "danger" | "info" | "neutral";
  link: string;
};

export type UpcomingRow = {
  transferId: string;
  name: string;
  at: number;
  what: string;
};

export type Estate = {
  transfers: { total: number; replicas: number; migrations: number; active: number; trend: number[] };
  deployments: { last30: number; completed: number; failed: number; running: number; perWeek: number[] };
  data: { bytes30: number; syncs30: number; machinesMigrated: number; perDay: number[] };
  success: { rate: number | null; completed: number; failed: number; perWeek: (number | null)[] };
  endpoints: { total: number; byProvider: { type: string; label: string; count: number }[] };
  agents: {
    pools: number;
    allocated: number;
    machines: number;
    inUse: number;
    errors: MinionPool[];
  };
  days: DayRow[];
  attention: AttentionItem[];
  routes: RouteRow[];
  top: { id: string; name: string; bytes: number; kind: string; source: string; target: string }[];
  upcoming: UpcomingRow[];
  activity: ActivityRow[];
};

const dayStart = (t: number) => {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const ago = (t: number, now: number) => formatAgo(t, now);

const title = (item: TransferItem | DeploymentItem) => getTransferItemTitle(item) || item.id;

/** The UTC times a Coriolis schedule (cron fields, dow 0 = Monday) fires between two instants. */
export const scheduleRuns = (info: ScheduleInfo | undefined, from: number, until: number, max = 50): number[] => {
  if (!info) {
    return [];
  }
  const out: number[] = [];
  const start = new Date(from);
  start.setUTCMinutes(0, 0, 0);
  for (let t = start.getTime(); t <= until && out.length < max; t += 60 * 60 * 1000) {
    const d = new Date(t);
    if (info.month != null && d.getUTCMonth() + 1 !== info.month) continue;
    if (info.dom != null && d.getUTCDate() !== info.dom) continue;
    if (info.dow != null && (d.getUTCDay() + 6) % 7 !== info.dow) continue;
    if (info.hour != null && d.getUTCHours() !== info.hour) continue;
    const at = t + (info.minute || 0) * 60 * 1000;
    if (at >= from && at <= until) {
      out.push(at);
    }
  }
  return out;
};

export const describeSchedule = (info: ScheduleInfo | undefined): string => {
  if (!info) return "";
  const days = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"];
  if (info.dom != null) return `monthly, day ${info.dom}`;
  if (info.dow != null) return `weekly, ${days[info.dow] || ""}`;
  if (info.hour != null) return "daily";
  return "hourly";
};

export const buildEstate = (input: EstateInput): Estate => {
  const { now, transfers, deployments, endpoints, pools, executions, copied, schedules } = input;
  const since = dayStart(now) - (DAYS - 1) * DAY_MS;

  // ── days ──
  const days: DayRow[] = Array.from({ length: DAYS }, (_, i) => ({
    day: since + i * DAY_MS,
    completed: 0,
    failed: 0,
    running: 0,
    bytes: 0,
  }));
  const dayIndex = (t: number | null) => {
    if (t == null || t < since) return -1;
    return Math.min(DAYS - 1, Math.floor((t - since) / DAY_MS));
  };
  const count = (status: string, at: number | null) => {
    const i = dayIndex(at);
    if (i < 0) return;
    if (status === "COMPLETED") days[i].completed += 1;
    else if (isFailedStatus(status)) days[i].failed += 1;
    else if (isActiveStatus(status)) days[i].running += 1;
  };

  let completed30 = 0;
  let failed30 = 0;
  let bytes30 = 0;
  let syncs30 = 0;
  const weekOf = (t: number) => Math.min(3, Math.floor((now - t) / (7 * DAY_MS)));
  const weekDone = [0, 0, 0, 0];
  const weekFail = [0, 0, 0, 0];

  const allExecs: { transfer: TransferItem; e: Execution }[] = [];
  transfers.forEach(t => {
    (executions[t.id] || []).forEach(e => allExecs.push({ transfer: t, e }));
  });

  allExecs.forEach(({ e }) => {
    if (e.type && e.type !== "transfer_execution") return;
    const start = parseApiDate(e.created_at);
    const end = parseApiDate(e.updated_at) || start;
    count(e.status, isActiveStatus(e.status) ? now : end);
    if (end != null && end >= now - DAYS * DAY_MS) {
      if (e.status === "COMPLETED") {
        completed30 += 1;
        syncs30 += 1;
        weekDone[weekOf(end)] += 1;
      } else if (isFailedStatus(e.status)) {
        failed30 += 1;
        weekFail[weekOf(end)] += 1;
      }
    }
    const bytes = copied[e.id];
    if (bytes && end != null) {
      const i = dayIndex(end);
      if (i >= 0) {
        days[i].bytes += bytes;
        bytes30 += bytes;
      }
    }
  });

  let deploy30 = 0;
  let deployDone = 0;
  let deployFailed = 0;
  let deployRunning = 0;
  let machinesMigrated = 0;
  const deployWeeks = [0, 0, 0, 0];
  deployments.forEach(d => {
    const start = parseApiDate(d.created_at);
    const end = parseApiDate(d.updated_at) || start;
    const status = d.last_execution_status;
    count(status, isActiveStatus(status) ? now : end);
    if (status === "COMPLETED") {
      machinesMigrated += (d.instances || []).length;
    }
    if (start != null && start >= now - DAYS * DAY_MS) {
      deploy30 += 1;
      deployWeeks[weekOf(start)] += 1;
      if (status === "COMPLETED") {
        deployDone += 1;
        completed30 += 1;
        weekDone[weekOf(end || start)] += 1;
      } else if (isFailedStatus(status)) {
        deployFailed += 1;
        failed30 += 1;
        weekFail[weekOf(end || start)] += 1;
      } else if (isActiveStatus(status)) {
        deployRunning += 1;
      }
    }
  });

  // ── transfers ──
  const replicas = transfers.filter(t => t.scenario !== "live_migration").length;
  const active =
    transfers.filter(t => isActiveStatus(t.last_execution_status)).length +
    deployments.filter(d => isActiveStatus(d.last_execution_status)).length;
  const created = transfers.map(t => parseApiDate(t.created_at) || 0).sort((a, b) => a - b);
  const trend = [3, 2, 1, 0].map(w => created.filter(c => c <= now - w * 7 * DAY_MS).length);

  // ── endpoints ──
  const byType = new Map<string, number>();
  endpoints.forEach(e => byType.set(e.type, (byType.get(e.type) || 0) + 1));
  const byProvider = Array.from(byType.entries())
    .map(([type, n]) => ({ type, label: input.providerLabel(type), count: n }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const endpointType = (id: string | null | undefined) => endpoints.find(e => e.id === id)?.type || null;

  // ── Swift Agents ──
  const allocated = pools.filter(p => p.status === "ALLOCATED").length;
  const machines = pools.reduce((a, p) => a + (p.minion_machines || []).length, 0);
  const inUse = pools.reduce(
    (a, p) => a + (p.minion_machines || []).filter(m => m.allocation_status === "IN_USE").length,
    0,
  );
  const agentErrors = pools.filter(
    p =>
      String(p.status).includes("ERROR") ||
      (p.minion_machines || []).some(m => String(m.allocation_status).includes("ERROR")),
  );

  // ── attention ──
  const attention: AttentionItem[] = [];
  transfers.forEach(t => {
    if (!isFailedStatus(t.last_execution_status)) return;
    const at = parseApiDate(t.updated_at) || parseApiDate(t.created_at) || now;
    if (now - at > 7 * DAY_MS) return;
    attention.push({
      severity: "critical",
      text: `${title(t)}: last ${t.scenario === "live_migration" ? "run" : "sync"} failed`,
      detail: ago(at, now),
      link: `/transfers/${t.id}/executions`,
      action: "See why",
    });
  });
  deployments.forEach(d => {
    if (!isFailedStatus(d.last_execution_status)) return;
    const at = parseApiDate(d.updated_at) || parseApiDate(d.created_at) || now;
    if (now - at > 7 * DAY_MS) return;
    attention.push({
      severity: "critical",
      text: `Deployment of ${title(d)} failed`,
      detail: ago(at, now),
      link: `/deployments/${d.id}/tasks`,
      action: "See why",
    });
  });
  transfers.forEach(t => {
    if (t.scenario === "live_migration" || isActiveStatus(t.last_execution_status)) return;
    const execs = executions[t.id];
    if (!execs) return;
    const lastGood = execs
      .filter(e => (!e.type || e.type === "transfer_execution") && e.status === "COMPLETED")
      .map(e => parseApiDate(e.updated_at) || parseApiDate(e.created_at) || 0)
      .sort((a, b) => b - a)[0];
    if (lastGood && now - lastGood > DAY_MS) {
      attention.push({
        severity: "warning",
        text: `${title(t)} has not synced for ${formatDuration((now - lastGood) / 1000).replace(/ 0?0 min$/, "")}`,
        detail: (schedules[t.id] || []).some(s => s.enabled) ? "Scheduled, but no successful sync since" : "No schedule",
        link: `/transfers/${t.id}/executions`,
        action: "Sync now",
      });
    } else if (!lastGood && !execs.length) {
      attention.push({
        severity: "info",
        text: `${title(t)} has never been synced`,
        link: `/transfers/${t.id}`,
        action: "Open",
      });
    }
  });
  (input.invalidEndpoints || []).forEach(e => {
    attention.push({
      severity: "warning",
      text: `Cloud endpoint ${e.name} fails validation`,
      link: `/endpoints/${e.id}`,
      action: "Check",
    });
  });
  agentErrors.forEach(p => {
    attention.push({
      severity: "critical",
      text: `SwiftIQ Agents ${p.name} ${p.status === "ERROR" ? "are in error" : "have a machine in error"}`,
      link: `/minion-pools/${p.id}`,
      action: "Open",
    });
  });
  const order = { critical: 0, warning: 1, info: 2 };
  attention.sort((a, b) => order[a.severity] - order[b.severity]);

  // ── routes ──
  const routeMap = new Map<string, RouteRow>();
  transfers.forEach(t => {
    const s = input.providerLabel(endpointType(t.origin_endpoint_id));
    const d = input.providerLabel(endpointType(t.destination_endpoint_id));
    const key = `${s}\u0000${d}`;
    const row = routeMap.get(key) || { source: s, target: d, transfers: 0, machines: 0 };
    row.transfers += 1;
    row.machines += (t.instances || []).length;
    routeMap.set(key, row);
  });
  const routes = Array.from(routeMap.values()).sort((a, b) => b.machines - a.machines || b.transfers - a.transfers);

  // ── top transfers by size ──
  const top = transfers
    .map(t => ({
      id: t.id,
      name: title(t),
      bytes: knownSizeOf(t),
      kind: t.scenario === "live_migration" ? "Migration" : "Replica",
      source: input.providerLabel(endpointType(t.origin_endpoint_id)),
      target: input.providerLabel(endpointType(t.destination_endpoint_id)),
    }))
    .filter(t => t.bytes > 0)
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, 5);

  // ── upcoming schedules ──
  const upcoming: UpcomingRow[] = [];
  transfers.forEach(t => {
    (schedules[t.id] || []).forEach(s => {
      if (s.enabled === false) return;
      const expires = s.expiration_date ? new Date(s.expiration_date).getTime() : null;
      const until = Math.min(now + 7 * DAY_MS, expires || Infinity);
      const runs = scheduleRuns(s.schedule, now, until, 8);
      runs.slice(0, 1).forEach(at =>
        upcoming.push({
          transferId: t.id,
          name: title(t),
          at,
          what: `${describeSchedule(s.schedule)}${runs.length > 1 ? ` · ${runs.length}${runs.length >= 8 ? "+" : ""} runs this week` : ""}`,
        }),
      );
    });
  });
  upcoming.sort((a, b) => a.at - b.at);

  // ── recent activity ──
  const activity: ActivityRow[] = [];
  allExecs.forEach(({ transfer, e }) => {
    const at = parseApiDate(e.updated_at) || parseApiDate(e.created_at);
    if (at == null) return;
    const what = transfer.scenario === "live_migration" ? "Migration run" : "Sync";
    let text = `${what} #${e.number} `;
    let tone: ActivityRow["tone"] = "neutral";
    if (e.status === "COMPLETED") {
      text += "completed";
      tone = "success";
    } else if (isFailedStatus(e.status)) {
      text += "failed";
      tone = "danger";
    } else if (isActiveStatus(e.status)) {
      text += "running";
      tone = "info";
    } else if (isCancelledStatus(e.status)) {
      text += "cancelled";
    } else {
      text += e.status.toLowerCase().replace(/_/g, " ");
    }
    activity.push({ at, text, detail: title(transfer), tone, link: `/transfers/${transfer.id}/executions` });
  });
  deployments.forEach(d => {
    const at = parseApiDate(d.updated_at) || parseApiDate(d.created_at);
    if (at == null) return;
    const s = d.last_execution_status;
    const tone: ActivityRow["tone"] =
      s === "COMPLETED" ? "success" : isFailedStatus(s) ? "danger" : isActiveStatus(s) ? "info" : "neutral";
    const verb =
      s === "COMPLETED" ? "completed" : isFailedStatus(s) ? "failed" : isActiveStatus(s) ? "running" : "cancelled";
    activity.push({ at, text: `Deployment ${verb}`, detail: title(d), tone, link: `/deployments/${d.id}/tasks` });
  });
  activity.sort((a, b) => b.at - a.at);

  const successRate = completed30 + failed30 ? (completed30 / (completed30 + failed30)) * 100 : null;
  return {
    transfers: { total: transfers.length, replicas, migrations: transfers.length - replicas, active, trend },
    deployments: { last30: deploy30, completed: deployDone, failed: deployFailed, running: deployRunning, perWeek: deployWeeks.slice().reverse() },
    data: { bytes30, syncs30, machinesMigrated, perDay: days.map(d => d.bytes) },
    success: {
      rate: successRate,
      completed: completed30,
      failed: failed30,
      perWeek: [3, 2, 1, 0].map(w => (weekDone[w] + weekFail[w] ? (weekDone[w] / (weekDone[w] + weekFail[w])) * 100 : null)),
    },
    endpoints: { total: endpoints.length, byProvider },
    agents: { pools: pools.length, allocated, machines, inUse, errors: agentErrors },
    days,
    attention,
    routes,
    top,
    upcoming: upcoming.slice(0, 6),
    activity: activity.slice(0, 8),
  };
};
